import React, { useState, useEffect, useRef } from 'react';
import { TextEscapeService, EscapeType } from '../../lib/services/textEscapeService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import './TextEscape.css';

export const TextEscape: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'escape' | 'unescape'>('escape');
  const [escapeType, setEscapeType] = useState<EscapeType>('html');
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const processingRef = useRef<string>('');

  const handleConvert = async () => {
    if (!input.trim()) {
      setOutput('');
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const processingKey = `${mode}-${escapeType}-${input.substring(0, 50)}`;
    processingRef.current = processingKey;

    try {
      let result;
      if (mode === 'escape') {
        result = await TextEscapeService.escape(input, escapeType);
      } else {
        result = await TextEscapeService.unescape(input, escapeType);
      }

      // Check if this is still the latest operation
      if (processingRef.current === processingKey) {
        setOutput(result.outputData as string);
        setError(null);
      }
    } catch (err) {
      if (processingRef.current === processingKey) {
        const errorMessage = ErrorHandler.handle(err, 'text-escape' as any);
        setError(errorMessage);
        setOutput('');
      }
    } finally {
      if (processingRef.current === processingKey) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (input) {
      const timer = setTimeout(() => {
        handleConvert();
      }, 300); // Debounce for real-time processing

      return () => clearTimeout(timer);
    } else {
      setOutput('');
      setError(null);
      setIsLoading(false);
    }
  }, [input, mode, escapeType]);

  const handleCopy = async () => {
    if (!output) return;

    try {
      const success = await ClipboardUtils.copyText(output);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy Result';
    }
  };

  const getButtonClassName = () => {
    const baseClass = 'copy-button';
    if (!output) return `${baseClass} copy-idle`;
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  return (
    <div className="text-escape">
      <noscript>
        <div className="fallback-form">
          <h3>JavaScript Required</h3>
          <p>Text escaping requires JavaScript. Please enable JavaScript to use this tool.</p>
        </div>
      </noscript>

      <div style={{ marginBottom: '2rem' }}>
        <h1>Text Escape/Unescape</h1>
        <p>
          Escape or unescape text for HTML entities, JavaScript/JSON strings, and URL encoding.
          Supports multiple escape formats for different use cases.
        </p>
      </div>

      <div className="encoder-container">
        <div className="form-group">
          <label>
            Mode:
          </label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as 'escape' | 'unescape')}
            data-testid="mode-select"
          >
            <option value="escape">Escape</option>
            <option value="unescape">Unescape</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            Escape Type:
          </label>
          <select
            value={escapeType}
            onChange={(e) => setEscapeType(e.target.value as EscapeType)}
            data-testid="escape-type-select"
          >
            <option value="html">HTML Entities</option>
            <option value="javascript">JavaScript/JSON</option>
            <option value="url">URL Encoding</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            {mode === 'escape' ? 'Text Input:' : 'Escaped Text Input:'}
          </label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={mode === 'escape' ? 'Enter text to escape...' : 'Enter escaped text to unescape...'}
            data-testid="input-text"
          />
        </div>

        {error && (
          <div className="status-message status-error">
            {error}
          </div>
        )}

        <div className="form-group">
          <label>
            {mode === 'escape' ? 'Escaped Output:' : 'Unescaped Output:'}
          </label>
          <textarea
            value={output}
            readOnly
            placeholder={isLoading ? 'Processing...' : 'Result will appear here...'}
            className={isLoading ? 'loading' : ''}
            data-testid="output-text"
          />
          {isLoading && (
            <div className="loading-indicator">Processing...</div>
          )}
        </div>

        <div className="button-container">
          <button
            ref={copyButtonRef}
            onClick={handleCopy}
            disabled={!output}
            className={getButtonClassName()}
            data-testid="copy-button"
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};
